import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { graphql, navigate } from "gatsby";
import styled from "styled-components";
import { faX, faFilter } from "@fortawesome/free-solid-svg-icons";
import { _ } from "../utils/i18n";
import Layout from "../components/layout";
import Container from "../components/container";
import SEO from "../components/seo";
import Breadcrumbs from "../components/breadcrumbs";
import ProductCategoryFilter from "../components/product-category-filter";
import ProductBlock from "../components/product-block";
import Button from "../components/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProductBrandsFilter from "../components/product-brands-filter";
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from "../context/global-context-provider";
import { parsePrice, sortProducts, updateDataLayer } from "../utils/helpers";
import ProductSorter from "../components/product-sorter-button";
import { HiddenOnPhone, HiddenOnDesktop } from "../components/hidden";
import Pagination from "../components/product-pagination";
import { useLocation } from "@reach/router";
import SearchResult from "../components/search-result";
import DefaultImage from "../images/ep_mobile.png";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-direction: column;
  }
`;
const RightSidePanel = styled.div`
  width: 70%;
  max-width: 1180px;
  padding-inline: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    padding-inline: 0;
  }
`;
const Title = styled.h1`
  font-size: clamp(1.5rem, 2.5vw, 2.5rem);
`;
const LeftSidePanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 30%;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
`;
const CategoryDescription = styled.div`
  margin-block: 2rem;
`;
const ProductsPagination = styled.div`
  padding-bottom: 0.5rem;
  width: 300px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
    padding-bottom: 1.5rem;
  }
`;
const FilterModal = styled.div`
  overflow-y: auto;
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: 100vw;
  height: 100%;
  z-index: 9999;
  transition-duration: 400ms;
  transition-property: transform, visibility;
  background-color: var(--color-white);
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(-100%)"};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
  &:last-child {
    padding-bottom: 2rem;
  }
`;
const ShowResultButton = styled.div`
  width: 100%;
  padding-inline: 1rem;
  margin-bottom: 2rem;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem;
`;
const ProductsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-rows: auto;
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const BreadcrumbsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
  width: 100%;
  padding: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    padding-inline: 0;
  }
`;
const ProductSorterWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 3rem 0 1rem 0;
`;

const ProductCategory = ({
  data: {
    category,
    allWpProductCategory,
    allWpProduct,
    productCategoryPage,
    privateCategoryPage,
    brands,
    wp,
  },
  pageContext,
}) => {
  const { search } = useLocation();
  pageContext.searchMode =
    pageContext.searchMode || new URLSearchParams(search).get("søkeord")
      ? true
      : false;
  const { seo } = category || {
    seo: {
      breadcrumbs: [
        {
          text: "Hjem",
          url: "/",
        },
        {
          text: "Søkeresultater",
          url: "/søkeresultater",
        },
      ],
    },
  };
  const { products, searchWord, searchResults } =
    useContext(GlobalStateContext);

  const dispatch = useContext(GlobalDispatchContext);
  const [isOpen, setOpen] = useState(false);
  const [isAllCategoriesPage, setIsAllCategoriesPage] = useState(true);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [order, setOrder] = useState("relevans");
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [isFirstLoading, setIsFirstLoading] = useState(pageContext.searchMode);
  let [currentPage, setCurrentPage] = useState(1);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const defaultPaginationLimit = 15;
  const limit =
    wp.crbThemeOptions.crbProductsPagination ?? defaultPaginationLimit;
  let startIndex = (currentPage - 1) * limit;
  let endIndex = startIndex + limit;
  const productLengthLimit = products.length + limit;
  if (productLengthLimit <= endIndex) {
    startIndex = 0;
    endIndex = startIndex + limit;
    currentPage = 1;
  }
  const displayedResults = sortProducts(order, products).slice(
    startIndex,
    endIndex
  );
  const totalCount = products.length;
  const productsWrapperRef = useRef(null);

  const searchModeBreadcrumbList = [...seo.breadcrumbs];
  searchModeBreadcrumbList.splice(1, 0, {
    text: "Søkeresultater",
    url: "/søkeresultater",
  });

  if (searchWord && searchWord.trim() !== "") {
    searchModeBreadcrumbList.forEach(
      (item) => (item.url += `?søkeord=${searchWord}`)
    );
  }

  const breadcrumbList =
    pageContext.searchMode && !category
      ? searchModeBreadcrumbList
      : isAllCategoriesPage
      ? seo.breadcrumbs
      : [
          seo.breadcrumbs[0],
          {
            text: productCategoryPage.name,
            url: productCategoryPage.uri,
          },
          {
            text: category?.title ?? "",
            url: category?.uri ?? "",
          },
        ];

  useEffect(() => {
    if (currentPage !== 1) {
      const productsWrapper = productsWrapperRef.current;
      if (productsWrapper) {
        const topPos =
          productsWrapper.getBoundingClientRect().top +
          window.pageYOffset -
          185;
        window.scrollTo({ top: topPos, behavior: "smooth" });
      }
    }
  }, [currentPage]);

  /** Remove Alla kategorier page from the categories list */
  const allCategories = allWpProductCategory?.nodes?.filter(
    (cat) =>
      cat.wpParent && cat.wpParent.node.crbMasterCategoryBusiness === true
  );

  const viewItemListEvent = (products) => {
    updateDataLayer(async () =>
      window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
          currency: wp.wcSettings.currency,
          value: 0,
          items: products?.map((product) => ({
            item_id: product?.databaseId,
            item_name: product?.name,
            item_category: product?.productCategories?.nodes?.[0]?.name,
            item_brand: product?.productBrands?.nodes?.[0]?.name,
            price:
              product?.price &&
              parsePrice(
                product.price,
                wp.wcSettings.priceThousandSeparator,
                wp.wcSettings.priceDecimalSeparator
              ),
            quantity: 1,
          })),
        },
      })
    );
  };

  const freeTextSearchMode = () => {
    if (isFirstLoading) {
      setIsFirstLoading(false);
      dispatch({ type: "UPDATE_PRODUCTS_BUSINESS", value: [] });
      return;
    }

    setIsAllCategoriesPage(false);

    const productTitles = searchResults.map((product) => product.name);
    const categories = category ? category.wpChildren.nodes : allCategories;

    if (category) {
      const filteredSearchResults = category.products.nodes.filter((item) =>
        productTitles.includes(item.name)
      );
      dispatch({
        type: "UPDATE_PRODUCTS_BUSINESS",
        value: filteredSearchResults,
      });
      viewItemListEvent(
        filteredSearchResults.filter(
          (item) =>
            item.nodeType === "SimpleProduct" ||
            item.nodeType === "VariableProduct"
        )
      );
      setFilteredProducts(filteredSearchResults);
    } else {
      dispatch({ type: "UPDATE_PRODUCTS_BUSINESS", value: searchResults });
      viewItemListEvent(
        searchResults.filter(
          (item) =>
            item.nodeType === "SimpleProduct" ||
            item.nodeType === "VariableProduct"
        )
      );
      setFilteredProducts(searchResults);
    }

    setFilteredCategories(
      structuredClone(categories).filter((category) => {
        if (category?.products?.nodes) {
          category.products.nodes = category.products.nodes.filter((product) =>
            productTitles.includes(product.name)
          );

          return category.products.nodes.length > 0;
        }
        return false;
      })
    );
  };

  /** Check if we are on a product category, Alla kategorie page or searh mode */
  useEffect(() => {
    if (category?.uri === productCategoryPage?.uri) {
      setIsAllCategoriesPage(true);
      dispatch({
        type: "UPDATE_PRODUCTS_BUSINESS",
        value: allWpProduct?.nodes,
      });
    } else if (pageContext.searchMode) {
      freeTextSearchMode();
    } else {
      setIsAllCategoriesPage(false);
      dispatch({
        type: "UPDATE_PRODUCTS_BUSINESS",
        value: category?.products?.nodes,
      });
      viewItemListEvent(category?.products?.nodes);
    }
  }, [pageContext?.searchMode, searchResults]);

  const checkPrivateOrBusiness = () => {
    const privateOrBusiness = localStorage.getItem("private-business");
    if (privateOrBusiness === "private" && !pageContext.searchMode) {
      navigate(privateCategoryPage.uri);
    } else if (privateOrBusiness === "private" && pageContext.searchMode) {
      const searchQueryParam = new URLSearchParams(location?.search).get(
        "søkeord"
      );
      navigate(`/nettbutikk/søkeresultater?søkeord=${searchQueryParam}`);
    }
  };

  useEffect(() => {
    window.addEventListener("storage", checkPrivateOrBusiness);
    return () => removeEventListener("storage", checkPrivateOrBusiness);
  }, []);

  return (
    <Layout>
      <FilterModal $isOpen={isOpen}>
        <ModalHeader>
          <h3 style={{ padding: "2rem 1rem", margin: "0" }}>{_("Filter")}</h3>
          <FontAwesomeIcon
            style={{ padding: "2rem 1rem" }}
            icon={faX}
            onClick={() => {
              setOpen(false);
            }}
          />
        </ModalHeader>

        <ProductCategoryFilter
          isModal={true}
          $isOpen={isOpen}
          category={category}
          searchMode={pageContext?.searchMode}
          categoryTitle={_("Søkeresultater")}
          productCategoryPage={productCategoryPage?.uri}
          allCategories={
            pageContext?.searchMode ? filteredCategories : allCategories
          }
        />
        {brands.nodes.length > 0 && (
          <ProductBrandsFilter
            brands={brands}
            allProducts={
              pageContext.searchMode
                ? filteredProducts
                : isAllCategoriesPage
                ? allWpProduct.nodes
                : category.products.nodes
            }
          />
        )}
        <ProductSorter
          isModal={true}
          isCompany
          onChange={(e) => {
            setOrder(e.target.value);
          }}
        />
        <ShowResultButton>
          <Button
            variant={"outlined"}
            onClick={() => {
              setOpen(false);
            }}
          >
            {_("Vis resultat")}
          </Button>
        </ShowResultButton>
      </FilterModal>

      <Container disableAnimation disableMargin>
        <BreadcrumbsWrapper>
          <Breadcrumbs breadcrumbList={breadcrumbList} />
        </BreadcrumbsWrapper>
        <Wrapper>
          <LeftSidePanel>
            <ProductCategoryFilter
              isModal={
                !isAllCategoriesPage ||
                (isAllCategoriesPage && category && pageContext?.searchMode)
              }
              category={category}
              searchMode={pageContext?.searchMode}
              categoryTitle={_("Søkeresultater")}
              productCategoryPage={productCategoryPage?.uri}
              allCategories={
                pageContext.searchMode ? filteredCategories : allCategories
              }
            />
            {brands.length > 0 && (
              <ProductBrandsFilter
                brands={brands}
                allProducts={
                  isAllCategoriesPage
                    ? pageContext.searchMode
                      ? filteredProducts
                      : allWpProduct.nodes
                    : category.products.nodes
                }
              />
            )}
          </LeftSidePanel>

          <RightSidePanel>
            <Title>
              {category && !pageContext.searchMode
                ? category.title
                : `${_("Søkeord")}: ${searchWord}`}
            </Title>
            <HiddenOnDesktop>
              <Button
                onClick={() => {
                  setOpen(true);
                }}
                variant={"outlined"}
              >
                {_("Filter")}
                <FontAwesomeIcon style={{ width: "3rem" }} icon={faFilter} />
              </Button>
            </HiddenOnDesktop>
            <ProductSorterWrapper>
              <HiddenOnPhone>
                <ProductSorter
                  isModal={false}
                  isCompany
                  onChange={(e) => {
                    setOrder(e.target.value);
                  }}
                />
              </HiddenOnPhone>
              <ProductsPagination>
                {startIndex + 1} - {startIndex + limit} av {totalCount}{" "}
                produkter
              </ProductsPagination>
            </ProductSorterWrapper>
            <ProductsWrapper ref={productsWrapperRef}>
              {displayedResults?.length > 0 ? (
                displayedResults.map((result, index) => (
                  <>
                    {!pageContext.searchMode ||
                    !result.nodeType ||
                    result.nodeType === "SimpleProduct" ||
                    result.nodeType === "VariableProduct" ? (
                      <ProductBlock
                        key={index}
                        showTitle
                        product={result}
                        showPrice
                        heartIconSize="small"
                      />
                    ) : (
                      <SearchResult
                        key={index}
                        title={result.title}
                        date={result.date}
                        excerpt={result.excerpt ?? result?.matches?.[0]}
                        link={result.uri}
                        postType={result.postStype}
                        image={result?.featuredImage?.node}
                        imgSrc={DefaultImage}
                        excerptLength={10}
                        parentCategoryName={result.parentCategoryName}
                      />
                    )}
                  </>
                ))
              ) : (
                <>
                  {pageContext.searchMode ? (
                    <div>{_("Ingen resultater funnet")}</div>
                  ) : (
                    <div>{_("Ingen produkter funnet")}</div>
                  )}
                </>
              )}
            </ProductsWrapper>
            <Pagination
              products={products}
              currentPage={currentPage}
              limit={limit}
              onPageChange={handlePageChange}
            />
            {category?.description && (
              <CategoryDescription
                dangerouslySetInnerHTML={{
                  __html: category.description,
                }}
              />
            )}
            {category?.crbLongDescription && (
              <CategoryDescription
                dangerouslySetInnerHTML={{
                  __html: category.crbLongDescription,
                }}
              />
            )}
          </RightSidePanel>
        </Wrapper>
      </Container>
    </Layout>
  );
};

ProductCategory.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object,
};

export default ProductCategory;

export const Head = ({ data: { category } }) => {
  const { seo, title } = category || {
    seo: {
      breadcrumbs: [
        {
          text: "Hjem",
          url: "/",
        },
        {
          text: "Søkeresultater",
          url: "/søkeresultater",
        },
      ],
    },
    title: "Søkeresultater",
  };
  return (
    <SEO
      title={seo?.title || title}
      description={seo?.metaDesc}
      breadcrumbs={seo?.breadcrumbs}
    />
  );
};

Head.propTypes = {
  data: PropTypes.any,
};

export const query = graphql`
  query ($id: String) {
    productCategoryPage: wpProductCategory(
      crbMasterCategoryBusiness: { eq: true }
    ) {
      id
      uri
      name
    }
    privateCategoryPage: wpProductCategory(crbMasterCategory: { eq: true }) {
      id
      uri
      name
    }
    allWpProductCategory: allWpProductCategory {
      nodes {
        title: name
        uri
        id
        image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        products {
          nodes {
            ...ProductEssentials
          }
        }
        wpParent {
          node {
            name
            uri
            crbMasterCategoryBusiness
          }
        }
        wpChildren {
          nodes {
            name
            uri
            id
            image {
              altText
              focalPoint {
                objectPosition
              }
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    }
    allWpProduct: allWpProduct {
      nodes {
        ...ProductEssentials
      }
    }
    category: wpProductCategory(id: { eq: $id }) {
      uri
      title: name
      id
      description
      crbLongDescription
      wpParent {
        node {
          name
          uri
        }
      }
      wpChildren {
        nodes {
          name
          uri
          id
          products {
            nodes {
              ...ProductEssentials
            }
          }
        }
      }
      seo {
        breadcrumbs {
          text
          url
        }
        title
        metaDesc
      }
      products {
        nodes {
          ...ProductEssentials
        }
      }
    }
    brands: allWpProductBrand {
      nodes {
        name
        slug
        id
        products {
          ...ProductEssentials
        }
      }
    }
    wp {
      crbThemeOptions {
        crbProductsPagination
      }
      wcSettings {
        currency
        priceDecimalSeparator
        priceThousandSeparator
      }
    }
  }
`;
